import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import Input from "components/atoms/Input";
import Icon from "components/atoms/Icon";
import MedicationItem from "components/molecules/MedicationItem";
import { Drug } from "common/models/types";
import Spinner from "components/atoms/Spinner";

interface MedicationListProps {
  drugs: Drug[];
  selectedDrug: Drug | null;
  onSelectDrug: (drug: Drug) => void;
  onSearch?: (query: string) => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

// 컨테이너 스타일
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border-right: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

// 검색 바 영역 스타일
const SearchContainer = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
`;

// 의약품 목록 영역 스타일
const ListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  position: relative;
`;

// 결과 없음 스타일
const NoResults = styled.div`
  padding: 16px;
  text-align: center;
`;

// 로딩 인디케이터
const LoaderWrapper = styled.div`
  padding: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MedicationList: React.FC<MedicationListProps> = ({
  drugs = [],
  selectedDrug,
  onSelectDrug,
  onSearch,
  hasMore,
  isLoadingMore,
  onLoadMore,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredDrugs, setFilteredDrugs] = useState<Drug[]>(drugs || []);
  const loaderRef = useRef<HTMLDivElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);

  // 검색어 변경 핸들러
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // 검색 실행 핸들러
  const handleSearch = () => {
    if (onSearch) {
      onSearch(searchQuery);
    }
  };

  // 키 입력 핸들러 (Enter 키 처리)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // 로컬 필터링은 onSearch가 없을 때만 적용 (onSearch가 있으면 서버 검색 사용)
  useEffect(() => {
    if (!onSearch) {
      if (!drugs) {
        setFilteredDrugs([]);
        return;
      }

      if (!searchQuery) {
        setFilteredDrugs(drugs);
      } else {
        const filtered = drugs.filter(
          (drug) =>
            drug.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (drug.mainIngredient &&
              drug.mainIngredient.toLowerCase().includes(searchQuery.toLowerCase()))
        );
        setFilteredDrugs(filtered);
      }
    } else {
      // 서버 검색을 사용할 경우 받아온 drugs를 그대로 표시
      setFilteredDrugs(drugs);
    }
  }, [searchQuery, drugs, onSearch]);

  // 서버에서 데이터가 변경될 때 항상 필터링된 약품 목록 업데이트
  useEffect(() => {
    setFilteredDrugs(drugs);
  }, [drugs]);

  // IntersectionObserver를 사용하여 무한 스크롤 구현
  useEffect(() => {
    if (!hasMore || isLoadingMore || !onLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      {
        threshold: 0.05,
        rootMargin: "20px",
      }
    );

    const currentLoader = loaderRef.current;
    if (currentLoader) {
      observer.observe(currentLoader);
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [hasMore, isLoadingMore, onLoadMore]);

  // 선택된 약품의 표준코드가 유효한지 확인하는 함수
  const isSelectedDrug = (drug: Drug): boolean => {
    if (!selectedDrug) return false;
    if (!selectedDrug.standardCode) return false;
    return drug.standardCode === selectedDrug.standardCode;
  };

  return (
    <Container>
      <SearchContainer>
        <Input
          type="text"
          placeholder="의약품명 또는 성분명으로 검색"
          value={searchQuery}
          onChange={handleSearchChange}
          onEnter={handleSearch}
          rightItems={[
            <Icon
              name="Search"
              size={20}
              key="search-icon"
              style={{ cursor: "pointer" }}
              onClick={handleSearch}
            />,
          ]}
        />
      </SearchContainer>
      <ListContainer ref={listContainerRef}>
        {filteredDrugs && filteredDrugs.length > 0 ? (
          <>
            {filteredDrugs.map((drug) => (
              <MedicationItem
                key={drug.standardCode || drug.itemCode}
                medication={drug}
                isSelected={isSelectedDrug(drug)}
                onClick={() => onSelectDrug(drug)}
              />
            ))}
            {hasMore && (
              <LoaderWrapper ref={loaderRef}>
                {isLoadingMore ? <Spinner /> : <div style={{ height: "10px" }}></div>}
              </LoaderWrapper>
            )}
          </>
        ) : (
          <NoResults>
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
              검색 결과가 없습니다
            </Text>
          </NoResults>
        )}
      </ListContainer>
    </Container>
  );
};

export default MedicationList;
